/** See docs/authz.md Usage and Reference. Principal is { role }; grants resolve once per session. */
import type { UserRole } from "@models/UserRole";

import type { OrgPolicy } from "./orgPolicy";
import { PERMISSION_DEFINITIONS, type PermissionId } from "./permissionIds";

export type Principal = {
  role: UserRole;
};

/** Permissions this principal holds. Membership is the whole decision. */
export type PermissionSet = ReadonlySet<PermissionId>;

/** Catalog keys, cached so resolve does not re-enumerate on every session. */
const PERMISSION_IDS = Object.keys(PERMISSION_DEFINITIONS) as PermissionId[];

/**
 * `ReadonlySet` and `Object.freeze` alone do not stop `Set#add` — the latter
 * only freezes own properties, not the internal collection. Seal the mutators
 * so a caller cannot grant process-wide by mutating a shared reference.
 */
function freezePermissionSet(set: Set<PermissionId>): PermissionSet {
  set.add = () => {
    throw new TypeError("PermissionSet is frozen");
  };
  set.delete = () => {
    throw new TypeError("PermissionSet is frozen");
  };
  set.clear = () => {
    throw new TypeError("PermissionSet is frozen");
  };
  return Object.freeze(set);
}

/** Shared sealed empty set — safe because mutators throw. */
const EMPTY_PERMISSION_SET = freezePermissionSet(new Set<PermissionId>());

export function emptyPermissionSet(): PermissionSet {
  return EMPTY_PERMISSION_SET;
}

/**
 * Resolves every grant for a principal in one pass, applying tenant overrides
 * over the defaults in permissionIds.ts.
 *
 * Eager by design: cost is permissions × roles once per session, and callers
 * (route filtering especially) ask many times against one resolved set.
 */
export function resolvePermissions(
  principal: Principal,
  policy: OrgPolicy
): PermissionSet {
  const granted = new Set<PermissionId>();

  for (const permissionId of PERMISSION_IDS) {
    const roles =
      policy.overrideGrants[permissionId] ??
      PERMISSION_DEFINITIONS[permissionId].defaultRoles;

    if (roles.includes(principal.role)) {
      granted.add(permissionId);
    }
  }

  return freezePermissionSet(granted);
}

/**
 * Unknown IDs deny by construction: the set is built from PERMISSION_DEFINITIONS,
 * so a string absent from that table is absent from the set. There is no runtime
 * allowlist check because `PermissionId` is a union and nothing untyped reaches
 * here — see docs/authz.md Known limits before adding one back.
 */
export function can(
  permissions: PermissionSet,
  permissionId: PermissionId
): boolean {
  return permissions.has(permissionId);
}
